import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiFilter,
  FiChevronLeft,
  FiSearch,
  FiX,
  FiGrid,
  FiList,
  FiChevronRight,
  FiChevronDown,
  FiSliders,
  FiTag,
  FiStar
} from "react-icons/fi";
import { useCart } from "../App";
import ProductCard from "./ProductCard";

const PRODUCTS_API = "https://server.havbit.in/api/public/products";
const CATEGORIES_API = "https://server.havbit.in/api/categories";
const SUBCATEGORIES_API = "https://server.havbit.in/api/subcategories";

export default function CategoryPage() {
  const { categoryName } = useParams();
  const navigate = useNavigate();
  const {
    cart,
    addToCart,
    updateQty,
    removeFromCart,
    wishlist = [],
    toggleWishlistItem
  } = useCart();

  const [category, setCategory] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [allSubcategories, setAllSubcategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [viewMode, setViewMode] = useState("grid");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [priceRange, setPriceRange] = useState([0, 5000]);
  const [selectedSubCategory, setSelectedSubCategory] = useState("all");
  const [subCategories, setSubCategories] = useState([]);
  const [maxPrice, setMaxPrice] = useState(5000);
  const [activeFilters, setActiveFilters] = useState([]);

  const scrollContainerRef = useRef(null);

  const cartQuantities = cart.reduce((acc, item) => {
    acc[item._id] = item.qty || 1;
    return acc;
  }, {});

  const isProductInWishlist = (productId) => {
    return wishlist.some(item => item._id === productId);
  };

  useEffect(() => {
    fetchCategoryData();
  }, [categoryName]);

  const fetchCategoryData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [productsRes, categoriesRes, subcategoriesRes] = await Promise.all([
        fetch(PRODUCTS_API),
        fetch(CATEGORIES_API),
        fetch(SUBCATEGORIES_API)
      ]);

      if (!productsRes.ok) throw new Error("Failed to fetch products");
      if (!categoriesRes.ok) throw new Error("Failed to fetch categories");
      if (!subcategoriesRes.ok) throw new Error("Failed to fetch subcategories");

      const productsResult = await productsRes.json();
      const categoriesData = await categoriesRes.json();
      const subcategoriesResult = await subcategoriesRes.json();
      const subcategoriesData = subcategoriesResult.data || [];

      setAllCategories(categoriesData);
      setAllSubcategories(subcategoriesData);

      const foundCategory = categoriesData.find(cat => cat._id === categoryName);
      if (foundCategory) {
        setCategory(foundCategory);
      } else {
        const decodedCategoryName = decodeURIComponent(categoryName || "");
        const foundByName = categoriesData.find(cat =>
          cat.name.toLowerCase() === decodedCategoryName.toLowerCase()
        );
        setCategory(foundByName || { name: decodedCategoryName, _id: null });
      }

      const productsArray = productsResult.data || [];
      const categoryProducts = productsArray.filter(product => {
        if (!product || !product.category) return false;
        return product.category === categoryName;
      });

      const subCategorySet = new Set();
      const productPrices = [];

      const relevantSubcategories = subcategoriesData.filter(sub =>
        sub.parent && sub.parent._id === categoryName
      );
      relevantSubcategories.forEach(sub => { if (sub.name) subCategorySet.add(sub.name); });

      categoryProducts.forEach(product => {
        if (product.subcategory) {
          const matchedSub = subcategoriesData.find(sub => sub._id === product.subcategory);
          if (matchedSub?.name) subCategorySet.add(matchedSub.name);
          else if (typeof product.subcategory === 'string') subCategorySet.add(product.subcategory.trim());
        }
        const price = product.price || 0;
        if (price > 0) productPrices.push(price);
      });

      // ✅ "all" ko LAST mein rakhna hai
      const withoutAll = Array.from(subCategorySet).sort();
      setSubCategories([...withoutAll, "all"]);

      if (productPrices.length > 0) {
        const roundedMax = Math.ceil(Math.max(...productPrices) / 100) * 100;
        setMaxPrice(Math.max(roundedMax, 1000));
        setPriceRange([0, Math.max(roundedMax, 1000)]);
      }

      const formattedProducts = categoryProducts.map(product => {
        const matchedCat = categoriesData.find(cat => cat._id === product.category);
        const catName = matchedCat ? matchedCat.name : "Uncategorized";

        let subcategoryName = "";
        if (product.subcategory) {
          const matchedSub = subcategoriesData.find(sub => sub._id === product.subcategory);
          subcategoryName = matchedSub?.name || (typeof product.subcategory === 'string' ? product.subcategory : "");
        }

        const price = product.price || 0;
        const originalPrice = product.oldPrice || 0;
        const discount = (originalPrice > 0 && price > 0 && originalPrice > price)
          ? Math.round(((originalPrice - price) / originalPrice) * 100) : 0;

        let imageUrl = product.image || (product.gallery?.[0]) || "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400";

        return {
          ...product,
          id: product.id || product._id,
          name: product.name || "",
          price, originalPrice, discount,
          image: imageUrl,
          category: catName,
          subcategory: subcategoryName,
          categoryId: product.category,
          subcategoryId: product.subcategory,
          stock: product.stock || 0,
          weight: product.customWeight || product.size || "100g",
          description: product.description || "",
          restaurantName: product.restaurantName || "",
          vendor: product.vendor || null,
          quality: product.quality || "Good",
          brandName: product.brandName || "",
          logo: product.logo || "",
          createdAt: product.createdAt || new Date().toISOString()
        };
      });

      setProducts(formattedProducts);
      setFilteredProducts(formattedProducts);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching category data:", error);
      setError(error.message);
      setProducts([]);
      setFilteredProducts([]);
      setSubCategories(["all"]);
      setLoading(false);
    }
  };

  useEffect(() => {
    let result = [...products];

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter(p =>
        (p.name || "").toLowerCase().includes(term) ||
        (p.description || "").toLowerCase().includes(term) ||
        (p.category || "").toLowerCase().includes(term) ||
        (p.subcategory || "").toLowerCase().includes(term) ||
        (p.restaurantName || "").toLowerCase().includes(term) ||
        (p.brandName || "").toLowerCase().includes(term)
      );
    }

    result = result.filter(p => {
      const price = p.price || 0;
      return price >= priceRange[0] && price <= priceRange[1];
    });

    if (selectedSubCategory !== "all") {
      result = result.filter(p => (p.subcategory || "").trim() === selectedSubCategory);
    }

    result.sort((a, b) => {
      switch (sortBy) {
        case "price-low": return (a.price || 0) - (b.price || 0);
        case "price-high": return (b.price || 0) - (a.price || 0);
        case "discount": return (b.discount || 0) - (a.discount || 0);
        case "name": return (a.name || "").localeCompare(b.name || "");
        case "newest": return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        default: return (b.stock || 0) - (a.stock || 0);
      }
    });

    setFilteredProducts(result);

    const filters = [];
    if (selectedSubCategory !== "all") filters.push(selectedSubCategory);
    if (priceRange[0] > 0 || priceRange[1] < maxPrice) filters.push(`₹${priceRange[0]}-₹${priceRange[1]}`);
    setActiveFilters(filters);
  }, [searchTerm, priceRange, selectedSubCategory, sortBy, products, maxPrice]);

  const handleAddToCart = (product, quantity = 1) => {
    addToCart({
      _id: product.id, name: product.name, price: product.price,
      originalPrice: product.originalPrice, image: product.image,
      category: product.category, subcategory: product.subcategory,
      weight: product.weight, brandName: product.brandName,
      restaurantName: product.restaurantName, qty: quantity
    }, quantity);
  };

  const handleQuantityUpdate = (productId, change) => {
    const newQty = (cartQuantities[productId] || 0) + change;
    if (newQty < 1) removeFromCart(productId);
    else updateQty(productId, newQty);
  };

  const handleToggleWishlist = (product) => {
    toggleWishlistItem({
      _id: product.id, name: product.name, price: product.price,
      originalPrice: product.originalPrice, image: product.image,
      category: product.category, subcategory: product.subcategory,
      weight: product.weight, discount: product.discount,
      restaurantName: product.restaurantName, brandName: product.brandName
    });
  };

  const totalSavings = filteredProducts.reduce((sum, p) => {
    const s = p.originalPrice - p.price;
    return sum + (s > 0 ? s : 0);
  }, 0);

  const totalItemsWithDiscount = filteredProducts.filter(p => p.discount > 0).length;
  const averageDiscount = totalItemsWithDiscount > 0
    ? Math.round(filteredProducts.reduce((sum, p) => sum + p.discount, 0) / totalItemsWithDiscount) : 0;

  const scrollCategories = (direction) => {
    const container = scrollContainerRef.current;
    if (container) container.scrollLeft += direction === 'left' ? -200 : 200;
  };

  const clearAllFilters = () => {
    setSelectedSubCategory("all");
    setPriceRange([0, maxPrice]);
    setSearchTerm("");
  };

  const removeFilter = (filter) => {
    if (filter.includes("₹")) setPriceRange([0, maxPrice]);
    else setSelectedSubCategory("all");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <div className="flex flex-col items-center justify-center h-96">
          <div className="w-8 h-8 border-3 border-red-200 border-t-red-600 rounded-full animate-spin mb-2"></div>
          <p className="text-xs text-gray-600 font-medium">Loading products...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center p-5 max-w-sm mx-4">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.698-.833-2.464 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-sm font-bold text-gray-900 mb-1.5">Oops! Something went wrong</h3>
          <p className="text-xs text-gray-600 mb-4">{error}</p>
          <div className="flex gap-2 justify-center">
            <button onClick={fetchCategoryData} className="px-4 py-2 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 shadow transition-all">Try Again</button>
            <button onClick={() => navigate('/')} className="px-4 py-2 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-200 transition-all">Go Home</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">

      {/* Back Button */}
      <div className="sticky top-0 z-50 bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-2.5 md:px-4 py-2">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-700 hover:text-red-600 transition-colors group">
            <div className="w-7 h-7 rounded-full bg-gray-100 group-hover:bg-red-100 flex items-center justify-center transition-colors">
              <FiChevronLeft size={16} />
            </div>
            <span className="text-xs font-medium">Back</span>
          </button>
        </div>
      </div>

      {/* Mobile Active Filters */}
      {activeFilters.length > 0 && (
        <div className="md:hidden bg-white border-b border-gray-200 px-2.5 py-1.5">
          <div className="flex items-center gap-1.5 overflow-x-auto flex-1">
            <span className="text-[10px] font-medium text-gray-700 whitespace-nowrap">Active:</span>
            {activeFilters.map((filter, index) => (
              <div key={index} className="flex items-center gap-0.5 bg-red-50 text-red-700 px-1.5 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap">
                {filter}
                <button onClick={() => removeFilter(filter)} className="ml-0.5"><FiX size={10} /></button>
              </div>
            ))}
            <button onClick={clearAllFilters} className="text-[10px] text-red-600 font-medium hover:text-red-700 whitespace-nowrap ml-1.5">Clear all</button>
          </div>
        </div>
      )}

      {/* Mobile SubCategory Slider */}
      {subCategories.length > 1 && (
        <div className="md:hidden bg-white border-b border-gray-200 sticky top-[72px] z-30 shadow-sm">
          <div className="relative px-2.5 py-1.5">
            <button onClick={() => scrollCategories('left')} className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-white shadow rounded-full w-6 h-6 flex items-center justify-center z-10 border border-gray-200">
              <FiChevronLeft size={14} className="text-gray-600" />
            </button>
            <div ref={scrollContainerRef} className="flex gap-1.5 overflow-x-auto scrollbar-hide px-4" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {subCategories.map((subCat) => (
                <button key={subCat} onClick={() => setSelectedSubCategory(subCat)}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${selectedSubCategory === subCat
                    ? 'bg-gradient-to-r from-red-600 to-red-500 text-white shadow shadow-red-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-900'}`}>
                  {subCat === "all" ? "All" : subCat}
                </button>
              ))}
            </div>
            <button onClick={() => scrollCategories('right')} className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-white shadow rounded-full w-6 h-6 flex items-center justify-center z-10 border border-gray-200">
              <FiChevronRight size={14} className="text-gray-600" />
            </button>
          </div>
        </div>
      )}

      {/* Desktop SubCategory Bar */}
      {subCategories.length > 1 && (
        <div className="hidden md:block bg-white border-b border-gray-200 shadow-sm">
          <div className="container mx-auto px-4 py-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <FiTag className="text-red-600" size={16} />
                <span className="text-xs font-medium text-gray-700 mr-2">Sub-Categories:</span>
                <div className="flex items-center gap-1.5">
                  {subCategories.map((subCat) => (
                    <button key={subCat} onClick={() => setSelectedSubCategory(subCat)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${selectedSubCategory === subCat
                        ? 'bg-gradient-to-r from-red-600 to-red-500 text-white shadow shadow-red-200'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-900'}`}>
                      {subCat === "all" ? "All Products" : subCat}
                    </button>
                  ))}
                </div>
              </div>
              {activeFilters.length > 0 && (
                <button onClick={clearAllFilters} className="text-xs text-red-600 font-medium hover:text-red-700">Clear filters</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Desktop Active Filters */}
      {activeFilters.length > 0 && (
        <div className="hidden md:block bg-red-50 border-b border-red-100">
          <div className="container mx-auto px-4 py-1.5">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-red-800">Active filters:</span>
              {activeFilters.map((filter, index) => (
                <div key={index} className="flex items-center gap-1.5 bg-white border border-red-200 text-red-700 px-2 py-1 rounded-lg text-xs font-medium">
                  {filter}
                  <button onClick={() => removeFilter(filter)} className="ml-0.5 hover:text-red-900"><FiX size={12} /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-2.5 md:px-4 py-2 md:py-4">
        {/* Desktop Controls */}
        <div className="hidden md:flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5 shadow-inner">
              <button onClick={() => setViewMode("grid")} className={`p-1.5 rounded-md transition-all ${viewMode === "grid" ? "bg-gradient-to-r from-red-600 to-red-500 text-white shadow" : "text-gray-600 hover:text-red-600 hover:bg-gray-200"}`}>
                <FiGrid size={14} />
              </button>
              <button onClick={() => setViewMode("list")} className={`p-1.5 rounded-md transition-all ${viewMode === "list" ? "bg-gradient-to-r from-red-600 to-red-500 text-white shadow" : "text-gray-600 hover:text-red-600 hover:bg-gray-200"}`}>
                <FiList size={14} />
              </button>
            </div>
            <div className="relative">
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                className="pl-3 pr-8 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-transparent appearance-none text-xs font-medium shadow-sm">
                <option value="newest">Newest First</option>
                <option value="price-high">Price: High to Low</option>
                <option value="price-low">Price: Low to High</option>
                <option value="discount">Best Discount</option>
                <option value="name">Name: A to Z</option>
                <option value="popularity">Most Popular</option>
              </select>
              <FiChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-white border border-gray-300 rounded-lg px-3 py-1.5">
              <div className="text-[10px] text-gray-600 font-medium">Price Range</div>
              <div className="text-xs font-bold text-red-600">₹{priceRange[0]} - ₹{priceRange[1]}</div>
            </div>
            <button onClick={() => setShowMobileFilters(true)} className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-red-600 to-red-500 text-white font-medium rounded-lg hover:shadow hover:shadow-red-200 transition-all shadow-sm">
              <FiSliders size={14} />
              <span className="text-xs">More Filters</span>
            </button>
          </div>
        </div>

        {/* Mobile Filter Indicator */}
        <div className="md:hidden mb-3 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <button onClick={() => setShowMobileFilters(true)} className="flex items-center gap-1 px-2 py-1.5 bg-white border border-red-200 rounded-lg text-red-600 text-xs font-medium shadow-sm">
              <FiSliders size={12} />
              Sort & Filter
            </button>
            {selectedSubCategory !== "all" && (
              <span className="text-[10px] bg-gradient-to-r from-red-600 to-red-500 text-white px-2 py-1 rounded-full font-medium shadow-sm">{selectedSubCategory}</span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-gray-600">{filteredProducts.length} items</span>
            {averageDiscount > 0 && (
              <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold">{averageDiscount}% OFF</span>
            )}
          </div>
        </div>

        {/* Products Display */}
        {filteredProducts.length === 0 ? (
          <div className="text-center py-8 bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
              <FiSearch className="text-red-600 text-xl" />
            </div>
            <h3 className="text-sm font-bold text-gray-900 mb-1.5">
              {searchTerm ? "No products found" : "No products in this category yet"}
            </h3>
            <p className="text-xs text-gray-600 mb-5 max-w-sm mx-auto">
              {searchTerm ? `We couldn't find any products matching "${searchTerm}"` : "Check back soon for new arrivals!"}
            </p>
            <button onClick={clearAllFilters} className="px-5 py-2 bg-gradient-to-r from-red-600 to-red-500 text-white font-medium rounded-lg hover:shadow hover:shadow-red-200 transition-all shadow-sm">
              {searchTerm ? "Clear Search" : "Browse Categories"}
            </button>
          </div>
        ) : viewMode === "list" && window.innerWidth >= 768 ? (
          <div className="space-y-2">
            {filteredProducts.map((product) => (
              <ProductListItem
                key={product.id} product={product}
                onAddToCart={handleAddToCart} onToggleWishlist={handleToggleWishlist}
                onQuantityUpdate={handleQuantityUpdate}
                isWishlisted={isProductInWishlist(product.id)}
                cartQuantity={cartQuantities[product.id] || 0} navigate={navigate}
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 md:gap-3">
            {filteredProducts.map((product) => (
              <ProductCard
                key={product.id} product={product}
                onAddToCart={handleAddToCart} onToggleWishlist={handleToggleWishlist}
                onQuantityUpdate={handleQuantityUpdate}
                isWishlisted={isProductInWishlist(product.id)}
                cartQuantity={cartQuantities[product.id] || 0} navigate={navigate}
              />
            ))}
          </div>
        )}
      </div>

      {/* Filters Modal */}
      <AnimatePresence>
        {showMobileFilters && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm" onClick={() => setShowMobileFilters(false)} />
            <motion.div
              initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25 }}
              className="absolute bottom-0 left-0 right-0 bg-white rounded-t-xl max-h-[80vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-white z-10 border-b border-gray-200 p-3">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-base font-bold text-gray-900">Filters & Sorting</h2>
                  <button onClick={() => setShowMobileFilters(false)} className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 hover:text-red-600">
                    <FiX size={16} />
                  </button>
                </div>
                {activeFilters.length > 0 && (
                  <div className="flex items-center gap-1.5 overflow-x-auto pb-1.5">
                    {activeFilters.map((filter, index) => (
                      <div key={index} className="flex items-center gap-0.5 bg-red-600 text-white px-2 py-1 rounded-full text-[10px] font-medium whitespace-nowrap">
                        {filter}
                        <button onClick={() => removeFilter(filter)} className="ml-0.5"><FiX size={10} /></button>
                      </div>
                    ))}
                    <button onClick={clearAllFilters} className="text-[10px] text-red-600 font-medium hover:text-red-700 whitespace-nowrap">Clear all</button>
                  </div>
                )}
              </div>

              <div className="p-3">
                <div className="mb-4">
                  <h3 className="font-bold text-gray-900 mb-2 text-sm">Sort By</h3>
                  <div className="space-y-1.5">
                    {[
                      { value: "newest", label: "Newest First" },
                      { value: "price-high", label: "Price: High to Low" },
                      { value: "price-low", label: "Price: Low to High" },
                      { value: "discount", label: "Best Discount" },
                      { value: "name", label: "Name: A to Z" },
                      { value: "popularity", label: "Most Popular" }
                    ].map((option) => (
                      <button key={option.value} onClick={() => setSortBy(option.value)}
                        className={`w-full text-left px-3 py-2 rounded-lg flex items-center justify-between text-xs ${sortBy === option.value ? "bg-gradient-to-r from-red-600 to-red-500 text-white shadow" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}>
                        <span className="font-medium">{option.label}</span>
                        {sortBy === option.value && (
                          <div className="w-4 h-4 rounded-full bg-white flex items-center justify-center">
                            <div className="w-1.5 h-1.5 rounded-full bg-red-600"></div>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {subCategories.length > 1 && (
                  <div className="mb-4">
                    <h3 className="font-bold text-gray-900 mb-2 text-sm">Sub-Categories</h3>
                    <div className="grid grid-cols-2 gap-1.5">
                      {subCategories.map((subCat) => (
                        <button key={subCat} onClick={() => setSelectedSubCategory(subCat)}
                          className={`px-3 py-2 rounded-lg text-center text-xs font-medium ${selectedSubCategory === subCat ? "bg-gradient-to-r from-red-600 to-red-500 text-white shadow" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}>
                          {subCat === "all" ? "All" : subCat}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-gray-900 text-sm">Price Range</h3>
                    <span className="text-xs font-bold text-red-600">₹{priceRange[0]} - ₹{priceRange[1]}</span>
                  </div>
                  <div className="space-y-2 px-1">
                    <input type="range" min="0" max={maxPrice} step="100" value={priceRange[1]}
                      onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                      className="w-full h-1.5 bg-gray-300 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-red-600 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow"
                    />
                    <div className="flex items-center justify-between text-[10px] text-gray-600">
                      <span>₹0</span><span>₹{maxPrice}</span>
                    </div>
                  </div>
                </div>

                <div className="sticky bottom-0 bg-white pt-3 border-t border-gray-200 pb-3">
                  <button onClick={() => setShowMobileFilters(false)} className="w-full py-3 bg-gradient-to-r from-red-600 to-red-500 text-white font-bold text-sm rounded-lg hover:shadow-lg hover:shadow-red-300 transition-all shadow">
                    Show {filteredProducts.length} Products
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Product List Item (Desktop only)
const ProductListItem = ({ product, onAddToCart, onToggleWishlist, onQuantityUpdate, isWishlisted, cartQuantity = 0, navigate }) => {
  const [imageError, setImageError] = useState(false);
  const handleAddClick = (e) => { e.stopPropagation(); onAddToCart(product, 1); };
  const handleIncrement = (e) => { e.stopPropagation(); onQuantityUpdate(product.id, 1); };
  const handleDecrement = (e) => { e.stopPropagation(); onQuantityUpdate(product.id, -1); };
  const handleProductClick = () => { if (product.id) navigate(`/product/${product.id}`); };
  const handleWishlistClick = (e) => { e.stopPropagation(); onToggleWishlist(product); };

  const savings = product.originalPrice > product.price ? product.originalPrice - product.price : 0;
  const isOutOfStock = product.stock === 0;
  const hasImage = product.image && product.image.trim() !== '';
  const discountPercentage = product.discount > 0 ? product.discount :
    (product.originalPrice > product.price ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100) : 0);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 hover:shadow-md transition-all duration-300 cursor-pointer" onClick={handleProductClick}>
      <div className="flex gap-3">
        <div className="flex-shrink-0 relative">
          <div className="w-16 h-16 rounded-lg overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
            {hasImage ? (
              <img src={product.image} alt={product.name} className="w-full h-full object-cover" onError={() => setImageError(true)} />
            ) : (
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            )}
            {discountPercentage > 0 && (
              <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-gradient-to-r from-red-600 to-red-500 text-white text-[10px] font-bold rounded-full">
                -{discountPercentage}%
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start mb-1.5">
            <div className="flex-1">
              <h4 className="text-sm font-bold text-gray-900 mb-1 line-clamp-1 hover:text-red-600">{product.name}</h4>
              <div className="flex items-center gap-1.5 mb-1.5">
                {product.subcategory && <span className="text-[10px] text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded-full">{product.subcategory}</span>}
                {product.quality && <span className="text-[10px] text-gray-500">{product.quality}</span>}
              </div>
              {(product.restaurantName || product.brandName) && (
                <p className="text-[10px] text-gray-500 mb-1.5">
                  {product.restaurantName ? `By ${product.restaurantName}` : ''}
                  {product.brandName ? ` • ${product.brandName}` : ''}
                </p>
              )}
              {product.weight && <span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-md">{product.weight}</span>}
            </div>
            <button onClick={handleWishlistClick} className="ml-1.5 flex-shrink-0">
              <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center hover:bg-red-50 transition-colors">
                <svg className={`w-4 h-4 ${isWishlisted ? 'text-red-500 fill-red-500' : 'text-gray-500 hover:text-red-500'}`} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                </svg>
              </div>
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-baseline gap-1.5">
                <p className="text-sm font-bold text-red-600">₹{product.price}</p>
                {product.originalPrice > product.price && (
                  <>
                    <p className="text-xs text-gray-500 line-through">₹{product.originalPrice}</p>
                    <p className="text-[10px] text-green-600 font-bold">Save ₹{savings}</p>
                  </>
                )}
              </div>
              <p className="text-[10px] text-gray-500 mt-0.5">
                {isOutOfStock ? "Out of Stock" : product.stock > 10 ? "In Stock" : `Only ${product.stock} left`}
              </p>
            </div>
            <div onClick={(e) => e.stopPropagation()}>
              {cartQuantity > 0 ? (
                <div className="flex items-center bg-gradient-to-r from-red-600 to-red-500 rounded-lg p-1 shadow-sm">
                  <button onClick={handleDecrement} className="w-6 h-6 flex items-center justify-center rounded-md bg-white/20 hover:bg-white/30 text-white">
                    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20 12H4" /></svg>
                  </button>
                  <span className="w-6 text-center font-bold text-white text-xs">{cartQuantity}</span>
                  <button onClick={handleIncrement} className="w-6 h-6 flex items-center justify-center rounded-md bg-white/20 hover:bg-white/30 text-white">
                    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
                  </button>
                </div>
              ) : (
                <button onClick={handleAddClick} disabled={isOutOfStock}
                  className={`px-3 py-1.5 rounded-lg font-bold text-xs transition-all shadow-sm ${isOutOfStock ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-gradient-to-r from-red-600 to-red-500 text-white hover:shadow hover:shadow-red-200"}`}>
                  {isOutOfStock ? "Out of Stock" : "Add to Cart"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};